import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import { useEditor } from '@tunasong/plugin-lib'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity } from '@tunasong/schemas'
import { getGenerationError, isPersisted } from '@tunasong/schemas'
import type { Node as SlateNode } from 'slate'
import { useReadOnly } from 'slate-react'
import { removeGeneratedBlock } from './remove-generated-block.js'

export interface GenerationFailedState {
  reason: string
  requestedByMe: boolean
  /** Undefined in a read-only editor */
  onRemove?: () => void
}

/**
 * Everything a generated block needs to render its failed state (GH #1958), or undefined
 * when the generation did not fail. `entity` is the freshest copy the block has — the loaded
 * entity for audio, the element itself for an image (RefBlock keeps its metadata current).
 *
 * Remove takes the block out of the document and, when the viewer is the account that
 * asked for it, trashes the stub too, so the empty mood sketch does not linger in the
 * folder listing. A bandmate only removes the block.
 */
export const useGenerationFailed = ({
  element,
  entity,
}: {
  element: SlateNode
  entity: Entity | undefined
}): GenerationFailedState | undefined => {
  const editor = useEditor()
  const readOnly = useReadOnly()
  const { userId } = useCurrentUser()
  const [deleteEntity] = graphHooks.useEntityDelete()

  const generationError = getGenerationError(entity)
  if (!generationError) {
    return undefined
  }
  const requestedByMe = Boolean(entity && isPersisted(entity) && userId && entity.userId === userId)

  const onRemove = () => {
    removeGeneratedBlock(editor, element)
    if (requestedByMe && entity && isPersisted(entity)) {
      deleteEntity({ entity }).catch((error: unknown) => {
        logger.warn('generation failed: the refused stub could not be trashed', { entityId: entity.id, error })
      })
    }
  }

  return { reason: generationError.reason, requestedByMe, onRemove: readOnly ? undefined : onRemove }
}
